import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Grid,
    Typography,
    CircularProgress
 } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";

// Utilitários para fontes de contato
import { getSourceIcon, getSourceColor, getSourceIconColor, isAdminSource } from "../../utils/contactSourceUtils";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { Form, Formik } from "formik";
import ShowTicketOpen from "../ShowTicketOpenModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { getWhatsappConnectionStatus } from '../../services/api';

// const filter = createFilterOptions({
// 	trim: true,
// });

const useStyles = makeStyles((theme) => ({
	autoComplete: { 
		width: "100%",
		marginBottom: 16
	},
	maxWidth: {
		width: "100%",
	},
	buttonColorError: {
		color: theme.palette.error.main,
		borderColor: theme.palette.error.main,
	},
	tagPreview: {
		display: "flex",
		alignItems: "center",
		padding: "8px 12px",
		borderRadius: "4px",
		marginTop: "8px",
		border: "2px solid transparent"
	},
	sectionTitle: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
		fontWeight: 500
	}
}));

// RENOMEAR O COMPONENTE E ADAPTAR PARA ACEITAR MÚLTIPLOS TICKETS
const AcceptTicketModal = ({ modalOpen, onClose, ticket, tickets = [], setLoading }) => {
	const history = useHistory();
	const classes = useStyles();
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedTag, setSelectedTag] = useState("");
	const [selectedContactSource, setSelectedContactSource] = useState("");
	const [contactSources, setContactSources] = useState([]);
	const [kanbanTags, setKanbanTags] = useState([]);
	const [loading, setLoadingLocal] = useState(false);
	const { user } = useContext(AuthContext);
	const [openAlert, setOpenAlert] = useState(false);
	const [userTicketOpen, setUserTicketOpen] = useState("");
	const [queueTicketOpen, setQueueTicketOpen] = useState("");
	const { tabOpen, setTabOpen } = useContext(TicketsContext);
	const { get: getSetting } = useCompanySettings();

	// Novo: ticketsToProcess pode ser um array de tickets ou um único ticket
	const isMultipleTickets = tickets.length > 0;
	const ticketsToProcess = isMultipleTickets ? tickets : [ticket].filter(Boolean);

	useEffect(() => {
		try {
			if (user.queues.length === 1) {
				setSelectedQueue(user.queues[0].id);
			}
			fetchKanbanTags();
			// Só carregar fontes de contato se não for bulk action
			if (!isMultipleTickets) {
				fetchContactSources();
			}
		} catch (err) {
			setLoadingLocal(false);
			toastError(err);
		}
	}, []);

	const fetchKanbanTags = async () => {
		try {
			const response = await api.get("/tag/kanban/");
			setKanbanTags(response.data.lista || []);
		} catch (error) {
			console.error('Erro ao carregar tags Kanban:', error);
			toastError(error);
		}
	};

	const fetchContactSources = async () => {
		try {
			const response = await api.get("/contact-sources");
			// A API retorna { records, count, hasMore }, mas precisamos apenas do array records
			const contactSourcesList = response.data.records || response.data || [];
			setContactSources(Array.isArray(contactSourcesList) ? contactSourcesList.filter(source => source.isActive) : []);
		} catch (error) {
			console.error('Erro ao carregar fontes de contato:', error);
			toastError(error);
		}
	};

	const handleClose = () => {
		setSelectedQueue("");
		setSelectedTag("");
		setSelectedContactSource("");
		onClose(); // Sem parâmetro para cancelamento
	};

	const handleCloseAlert = () => {
		setOpenAlert(false);
		setLoadingLocal(false)
	};


	const handleSendMessage = async (id) => {
		let isGreetingMessage = false;
		try {
			const setting = await getSetting({ "column": "sendGreetingAccepted" });
			if (setting.sendGreetingAccepted === "enabled") isGreetingMessage = true;
		} catch (err) {
			toastError(err);
		}
		let settingMessage;
		try {
			settingMessage = await getSetting({ "column": "greetingAcceptedMessage" })
		} catch (err) {
			toastError(err);
		}
		if (isGreetingMessage) {
			const msg = `${settingMessage.greetingAcceptedMessage}`;
			const message = {
				read: 1,
				fromMe: true,
				mediaUrl: "",
				body: `${msg.trim()}`,
			};
			try {
				await api.post(`/messages/${id}`, message);
			} catch (err) {
				toastError(err);
			}
		}
	};

	const handleAcceptTickets = async () => {
		setLoadingLocal(true);
		setLoading && setLoading(true);
		
		// Validação obrigatória da fonte de contato apenas quando há contatos sem fonte E não é bulk action
		if (!isMultipleTickets) {
			const hasContactsWithoutSource = ticketsToProcess.some(t => !t.contact?.contactSource?.id);
			if (hasContactsWithoutSource && !selectedContactSource) {
				toastError("É obrigatório selecionar uma fonte de contato.");
				setLoadingLocal(false);
				setLoading && setLoading(false);
				return;
			}
		}
		
		try {
			// Validação da conexão do WhatsApp para todos os tickets
			for (const t of ticketsToProcess) {
				if (t.whatsapp && t.whatsapp.id) {
					const status = await getWhatsappConnectionStatus(t.whatsapp.id);
					if (status !== 'CONNECTED') {
						setLoadingLocal(false);
						setLoading && setLoading(false);
						toastError('A conexão do WhatsApp não está ativa. Não é possível aceitar o ticket.');
						return;
					}
				}
			}

			if (isMultipleTickets) {
				// Aceitar múltiplos tickets usando o endpoint bulk
				const ticketIds = ticketsToProcess.map(t => t.id);
				
				// 1. Aceitar todos os tickets em massa
				await api.put("/tickets", {
					ticketIds,
					status: "open",
					userId: user?.id || null,
					queueId: selectedQueue
				});

				// 2. Adicionar tag Kanban em massa
				await api.post("/ticket-tags/bulk", {
					ticketIds,
					tagId: selectedTag
				});

				// 3. Enviar mensagens de saudação para todos os tickets
				const messagePromises = ticketsToProcess.map(t => handleSendMessage(t.id));
				await Promise.all(messagePromises);
			} else {
				// Lógica individual (mantém compatibilidade)
				const t = ticketsToProcess[0];
				await api.put(`/tickets/${t.id}`, {
					status: t.isGroup && t.channel === 'whatsapp' ? "group" : "open",
					userId: user?.id || null,
					queueId: selectedQueue
				});
				
				await api.put(`/ticket-tags/${t.id}/${selectedTag}`);
				
				// Atualizar fonte de contato (apenas quando o contato não tem fonte)
				if (!t.contact?.contactSource?.id) {
					await api.put(`/contacts/${t.contact.id}/contactSource`, { 
						contactSourceId: selectedContactSource 
					});
				}
				
				await handleSendMessage(t.id);
			}

			setLoadingLocal(false);
			setLoading && setLoading(false);
			setTabOpen("open");
			onClose("success"); // Passa um valor truthy como a modal de Arquivar
			
			// Feedback
			if (isMultipleTickets) {
				window.toast && window.toast.success(`${ticketsToProcess.length} tickets aceitos com sucesso!`);
			} else {
				window.toast && window.toast.success(`Ticket aceito com sucesso!`);
			}
		} catch (err) {
			setLoadingLocal(false);
			setLoading && setLoading(false);
			toastError(err);
		}
	};

	const selectedTagData = kanbanTags.find(tag => tag.id === selectedTag);

	// Verificar se há contatos sem fonte de contato (para mostrar campo de fonte) - apenas para tickets individuais
	const hasContactsWithoutSource = !isMultipleTickets && ticketsToProcess.some(t => !t.contact?.contactSource?.id);

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle id="form-dialog-title">
				{isMultipleTickets ? `Aceitar ${ticketsToProcess.length} tickets` : "Aceitar"}
			</DialogTitle>
			<DialogContent dividers style={{ position: 'relative', minHeight: 180, maxHeight: 400, overflowY: 'auto' }}>
				{loading && (
					<div style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background: 'rgba(255,255,255,0.7)',
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
						<CircularProgress size={48} color="primary" />
					</div>
				)}
				<Grid container spacing={3} style={{ opacity: loading ? 0.5 : 1 }}>
					<Grid item xs={12}>
						<Typography variant="subtitle2" className={classes.sectionTitle}>
							Fila do atendimento
						</Typography>
						<FormControl variant="outlined" className={classes.maxWidth} required>
							<InputLabel>{i18n.t("ticketsList.acceptModal.queue")}</InputLabel>
							<Select
								value={selectedQueue}
								onChange={(e) => setSelectedQueue(e.target.value)}
								label={i18n.t("ticketsList.acceptModal.queue")}
							>
								<MenuItem value={''}>&nbsp;</MenuItem>
								{user.queues.map((queue) => (
									<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<Typography variant="subtitle2" className={classes.sectionTitle}>
							Categorização CRM
						</Typography>
						<FormControl variant="outlined" className={classes.maxWidth} required>
							<InputLabel>Selecionar categoria *</InputLabel>
							<Select
								value={selectedTag}
								onChange={(e) => setSelectedTag(e.target.value)}
								label="Selecionar categoria *"
								MenuProps={{
									PaperProps: {
										style: {
											maxHeight: 200,
											overflowY: 'auto'
										}
									}
								}}
							>
								<MenuItem value={''}>
									<em>Selecione uma categoria</em>
								</MenuItem>
								{kanbanTags.map((tag) => (
									<MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
								))}
							</Select>
						</FormControl>
						{/* Preview da tag selecionada */}
						{selectedTagData && (
							<div 
								className={classes.tagPreview}
								style={{ 
									backgroundColor: selectedTagData.color + '20',
									borderColor: selectedTagData.color 
								}}
							>
								<Typography variant="body2">
									Ticket será categorizado como: <strong>{selectedTagData.name}</strong>
								</Typography>
							</div>
						)}
					</Grid>
					{/* Fonte do Contato - apenas para tickets individuais */}
					{!isMultipleTickets && (
						<Grid item xs={12}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>
								Fonte do Contato
							</Typography>
							{hasContactsWithoutSource && (
								<FormControl variant="outlined" className={classes.maxWidth} required>
									<InputLabel>Selecionar fonte do contato *</InputLabel>
									<Select
										value={selectedContactSource}
										onChange={(e) => setSelectedContactSource(e.target.value)}
										label="Selecionar fonte do contato *"
									>
										<MenuItem value={''}>
											<em>Selecione uma fonte (obrigatório)</em>
										</MenuItem>
										{contactSources.map((source) => (
											<MenuItem key={source.id} value={source.id}>
												<span style={{ 
													display: "flex", 
													alignItems: "center",
													color: source.isFixed ? "#1976d2" : "inherit",
													fontWeight: source.isFixed ? "bold" : "normal"
												}}>
													{source.isFixed && getSourceIcon(source.name) && (() => {
														const sourceInfo = getSourceIcon(source.name);
														const IconComponent = sourceInfo.icon;
														
														return (
															<span style={{ marginRight: "8px" }}>
																<IconComponent 
																	style={{ 
																		fontSize: "18px", 
																		color: isAdminSource(source.name) ? "#000000" : getSourceIconColor(source.name)
																	}} 
																/>
															</span>
														);
													})()}
													{source.name}
												</span>
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
							{/* Preview da fonte selecionada */}
							{selectedContactSource && (() => {
								const selectedSource = contactSources.find(s => s.id === selectedContactSource);
								return selectedSource ? (
									<div 
										className={classes.tagPreview}
										style={{ 
											backgroundColor: "#e3f2fd",
											borderColor: "#1976d2" 
										}}
									>
										<Typography variant="body2">
											Fonte do contato: <strong>{selectedSource.name}</strong>
											{selectedSource.description && ` - ${selectedSource.description}`}
										</Typography>
									</div>
								) : null;
							})()}
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary" variant="outlined" disabled={loading}>
					Cancelar
				</Button>
				<ButtonWithSpinner
					variant="contained"
					color="primary"
					onClick={handleAcceptTickets}
					loading={false} // nunca mostra spinner no botão
					disabled={!selectedQueue || !selectedTag || (!isMultipleTickets && hasContactsWithoutSource && !selectedContactSource) || loading}
				>
					{isMultipleTickets ? `Aceitar ${ticketsToProcess.length} tickets` : "Aceitar"}
				</ButtonWithSpinner>
			</DialogActions>
		</Dialog>
	);
};

export default AcceptTicketModal;